import moment from 'moment';
import { Verb, trackUe, Noun } from '@trello/analytics';
import { hasAdvancedChecklists } from './has-advanced-checklists';

interface Member {
  get: (attr: 'username') => string;
}

interface Organization {
  isFeatureEnabled: (feature: string) => boolean;
}

interface Board {
  memberList: Member[];
  getOrganization: () => Organization | null;
}

export function getAssignee(name: string, board: Board): Member | null {
  if (!hasAdvancedChecklists(board)) {
    return null;
  }

  const initialMention = name.match(/^@([a-z0-9_]{3,})\b/)?.[1];

  const assignee =
    initialMention &&
    board.memberList.find(
      (member) => member.get('username') === initialMention,
    );

  return assignee || null;
}

export function getDueDate(name: string, board: Board) {
  if (!hasAdvancedChecklists(board)) {
    return null;
  }

  const potentialDueDate = name.match(/\b\d\d\d\d-\d\d-\d\d$/)?.[0];

  const momentDate = potentialDueDate && moment(potentialDueDate);

  const due = momentDate && momentDate.isValid() ? momentDate.toDate() : null;

  return due;
}

export function trackNameChange(
  prevName: string,
  newName: string,
  trackingMethod: string,
  board: Board,
) {
  const prevAssignee = getAssignee(prevName, board);
  const prevDueDate = getDueDate(prevName, board);

  const newAssignee = getAssignee(newName, board);
  const newDueDate = getDueDate(newName, board);

  if (
    (prevAssignee || newAssignee) &&
    prevAssignee?.get('username') !== newAssignee?.get('username')
  ) {
    let verb = Verb.CHANGES;
    if (!prevAssignee && newAssignee) {
      verb = Verb.ADDS;
    } else if (prevAssignee && !newAssignee) {
      verb = Verb.REMOVES;
    }
    trackUe(
      Noun.CARD_DETAIL,
      verb,
      Noun.MEMBER,
      'on checkitem',
      '',
      trackingMethod,
    );
  }

  if (
    (prevDueDate || newDueDate) &&
    prevDueDate?.getTime() !== newDueDate?.getTime()
  ) {
    let verb = Verb.CHANGES;
    if (!prevDueDate && newDueDate) {
      verb = Verb.ADDS;
    } else if (prevDueDate && !newDueDate) {
      verb = Verb.REMOVES;
    }
    trackUe(
      Noun.CARD_DETAIL,
      verb,
      Noun.DUE_DATE,
      'on checkitem',
      '',
      trackingMethod,
    );
  }
}

export function trackNameRender(name: string, board: Board) {
  const prepObj = hasAdvancedChecklists(board) ? 'on advanced checklists' : '';

  trackUe(
    Noun.CARD_DETAIL,
    Verb.RENDERS,
    Noun.CHECKLIST_ITEM,
    prepObj,
    '',
    'by viewing card detail',
  );

  if (getAssignee(name, board)) {
    trackUe(
      Noun.CARD_DETAIL,
      Verb.RENDERS,
      Noun.MEMBER,
      'on checkitem',
      '',
      'by having an mention at the beginning of checkitem text',
    );
  }

  if (getDueDate(name, board)) {
    trackUe(
      Noun.CARD_DETAIL,
      Verb.RENDERS,
      Noun.DUE_DATE,
      'on checkitem',
      '',
      'by having a date-like string at the end of checkitem text',
    );
  }
}
